/**
 * Plugin for handling SSO gate token extraction from callback URLs
 * and injection into subsequent API requests
 */

import { AUTHORISED_CONTENT_ENABLED } from '~~/layers/core/app/util/features'

const GATE_TOKENS_KEY = 'digistorm:gate-tokens'

const GATE_TOKEN_PARAM = 'gate_token'
const GATE_HANDLE_PARAM = 'gate_handle'

export default defineNuxtPlugin(async (_nuxtApp) => {
    // Only run on client side
    if (import.meta.server) return

    if (!AUTHORISED_CONTENT_ENABLED) return

    /**
     * Extract gate tokens from URL and clean up the URL
     */
    function extractGateTokensFromUrl() {
        // Check if URL has gate parameters
        const url = new URL(window.location.href)
        const gateToken = url.searchParams.get(GATE_TOKEN_PARAM)
        const gateHandle = url.searchParams.get(GATE_HANDLE_PARAM)

        // If there are gate parameters, extract and store them
        if (gateToken && gateHandle) {
            try {
                // Get existing tokens
                const existingTokens = localStorage.getItem(GATE_TOKENS_KEY)
                const tokens = existingTokens ? JSON.parse(existingTokens) : {}

                // Store new token
                tokens[gateHandle] = gateToken

                // Persist to localStorage
                localStorage.setItem(GATE_TOKENS_KEY, JSON.stringify(tokens))

                // Clean up URL by removing gate parameters
                url.searchParams.delete(GATE_TOKEN_PARAM)
                url.searchParams.delete(GATE_HANDLE_PARAM)

                // Navigate to the cleaned URL (using replace so it doesn't create a history entry)
                window.location.replace(url.toString())
            } catch (error) {
                console.error('[Gate Token] Error processing gate tokens:', error)
            }
        }
    }

    /**
     * Get all gate tokens from localStorage as a comma-separated string
     */
    function getGateToken(): string | null {
        try {
            const storedTokens = localStorage.getItem(GATE_TOKENS_KEY)
            if (!storedTokens) return null

            const tokens = JSON.parse(storedTokens)
            const values = Object.values(tokens).filter((t): t is string => typeof t === 'string' && t !== '')

            return values.length > 0 ? values.join(',') : null
        } catch (error) {
            console.error('[Gate Token] Error retrieving gate token:', error)
        }
        return null
    }

    /**
     * Intercept fetch requests to inject gate token headers
     */
    function setupFetchInterceptor() {
        const originalFetch = window.fetch

        window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
            // Get or create the init object
            const options: RequestInit = init || {}

            // Ensure headers object exists
            if (!options.headers) {
                options.headers = {}
            }

            // Convert headers to a mutable object if it's a Headers instance
            let headers = options.headers
            if (headers instanceof Headers) {
                const newHeaders: Record<string, string> = {}
                headers.forEach((value, key) => {
                    newHeaders[key] = value
                })
                headers = newHeaders
            }

            // Add gate token if available
            const gateToken = getGateToken()
            if (gateToken) {
                ;(headers as Record<string, string>)['x-gate-token'] = gateToken
            }

            options.headers = headers

            return originalFetch.call(this, input, options)
        } as typeof window.fetch
    }

    // Extract tokens on initial page load and setup fetch interceptor immediately
    // (no need for nextTick - we're not touching the DOM, just URL and localStorage)
    extractGateTokensFromUrl()
    setupFetchInterceptor()

    return {
        provide: {
            extractGateTokensFromUrl,
            getGateToken,
            setupFetchInterceptor,
        },
    }
})
